import type { ExpEstimatedEvidence, ExpRateChartSnapshot, PercentageExpStatistics } from '@/domain/types'

/** Isolated units and levels: these samples can never reach an EXP ledger. */
export class PercentageExpProgress {
  private samples: { at: number; gained: number }[] = []
  private first: ExpEstimatedEvidence | null = null
  private last: ExpEstimatedEvidence | null = null
  private lossOffset = 0
  private gap = false
  private freshnessMs = 15_000

  setSamplingInterval(ms: number): void { if (Number.isFinite(ms) && ms > 0) this.freshnessMs = Math.max(15_000, ms * 3) }

  reset(): void { this.first = this.last = null; this.samples = []; this.lossOffset = 0; this.gap = false }

  breakContinuity(): void { this.gap = true }

  observe(sample: ExpEstimatedEvidence, rebaseline = false): void {
    if (sample.sourceAt <= (this.last?.sourceAt ?? -Infinity)) return
    if (sample.value.absolute !== null || sample.level == null || sample.value.ratio == null) { this.reset(); return }
    if (this.gap || rebaseline || sample.level !== this.last?.level || sample.value.sourceId !== this.last?.value.sourceId
      || sample.sourceAt - (this.last?.sourceAt ?? sample.sourceAt) > this.freshnessMs) this.reset()
    this.first ??= sample
    if (this.last && sample.value.ratio < this.last.value.ratio!) this.lossOffset += this.last.value.ratio! - sample.value.ratio
    this.last = sample
    const gained = (sample.value.ratio - this.first.value.ratio! + this.lossOffset) * 100
    this.samples.push({ at: sample.sourceAt, gained })
    while (this.samples.length > 2 && this.samples[1]!.at < sample.sourceAt - 60_000) this.samples.shift()
  }

  snapshot(): PercentageExpStatistics | undefined {
    if (!this.first || !this.last) return undefined
    const durationMs = this.last.sourceAt - this.first.sourceAt
    const gained = this.samples.at(-1)!.gained
    const perHour = durationMs > 0 ? gained / durationMs * 3_600_000 : null
    return { unit: 'percentage-points', level: this.last.level!, ratio: this.last.value.ratio!, gained, durationMs, perHour,
      sourceAt: this.last.sourceAt, etaMs: this.last.level! >= 100 || !perHour || perHour <= 0 ? null
        : (1 - this.last.value.ratio!) * 100 / perHour * 3_600_000 }
  }

  chart(now: number): ExpRateChartSnapshot | undefined {
    const stats = this.snapshot(), first = this.samples[0], last = this.samples.at(-1)
    if (!stats || !first || !last) return undefined
    const stale = now - last.at > this.freshnessMs
    const duration = last.at - first.at
    const ready = !stale && duration >= 60_000
    const value = ready ? (last.gained - first.gained) / duration * 3_600_000 : null
    return { unit: 'percentage-points', approximate: true, from: first.at, to: now, average: stats.perHour,
      points: ready ? [{ at: last.at, value, segment: 0 }] : [], gaps: [],
      recent: { approximate: true, value, at: last.at, windowMs: 60_000, continuousMs: duration,
        status: stale ? 'stale' : ready ? 'ready' : 'warming' } }
  }
}
